import React from 'react';
import { FiArrowLeft, FiX, FiCheck, FiChevronRight } from 'react-icons/fi';
import { 
  LuSmartphone, 
  LuHouse, 
  LuBriefcase, 
  LuTruck, 
  LuCar, 
  LuGrid2X2, 
  LuShirt,
  LuBook,
  LuUtensils,
  LuHeart,
  LuMusic,
  LuDumbbell,
  LuBaby,
  LuWrench
} from 'react-icons/lu';

const CategoryModal = ({ 
  form, 
  mainCategories, 
  subcategories, 
  allCategories,
  onMainCategorySelect, 
  onSubcategorySelect, 
  onClose,
  isMobile = false
}) => {
  
  // Get appropriate icon based on category name
  const getCategoryIcon = (category) => {
    if (!category?.name) return <LuGrid2X2 className="w-5 h-5" />;
    
    const name = category.name.toLowerCase();
    
    if (name.includes('electronics') || name.includes('mobile') || name.includes('phone') || name.includes('computer')) {
      return <LuSmartphone className="w-5 h-5" />;
    }
    if (name.includes('vehicle') || name.includes('car') || name.includes('auto')) {
      return <LuCar className="w-5 h-5" />;
    }
    if (name.includes('service') || name.includes('professional')) {
      return <LuBriefcase className="w-5 h-5" />;
    }
    if (name.includes('property') || name.includes('real estate') || name.includes('rent')) {
      return <LuHouse className="w-5 h-5" />;
    }
    if (name.includes('fashion') || name.includes('clothing') || name.includes('shoes')) {
      return <LuShirt className="w-5 h-5" />;
    }
    if (name.includes('book') || name.includes('education') || name.includes('learning')) {
      return <LuBook className="w-5 h-5" />;
    }
    if (name.includes('food') || name.includes('restaurant') || name.includes('drink')) {
      return <LuUtensils className="w-5 h-5" />;
    }
    if (name.includes('health') || name.includes('beauty') || name.includes('medical')) {
      return <LuHeart className="w-5 h-5" />;
    }
    if (name.includes('entertainment') || name.includes('music') || name.includes('movie')) {
      return <LuMusic className="w-5 h-5" />;
    }
    if (name.includes('sport') || name.includes('fitness') || name.includes('gym')) {
      return <LuDumbbell className="w-5 h-5" />;
    }
    if (name.includes('baby') || name.includes('child') || name.includes('kid')) {
      return <LuBaby className="w-5 h-5" />;
    }
    if (name.includes('tool') || name.includes('equipment') || name.includes('hardware')) {
      return <LuWrench className="w-5 h-5" />;
    }
    if (name.includes('truck') || name.includes('transport') || name.includes('logistics')) {
      return <LuTruck className="w-5 h-5" />;
    }
    
    return <LuGrid2X2 className="w-5 h-5" />;
  };

  // Brand colors
  const colors = {
    primary: '#ff660e',
    primaryLight: '#fff5f0',
    primaryDark: '#e65c0d',
    text: '#0f0e0e',
    textLight: '#666666',
    border: '#e5e5e5',
    hoverBg: '#f8f9fa'
  };

  const containerClass = isMobile 
    ? "bg-white rounded-2xl w-full max-w-sm shadow-xl"
    : "bg-white rounded-2xl w-full max-w-md shadow-2xl";
    
  const headerClass = isMobile 
    ? "flex items-center justify-between p-4 border-b"
    : "flex items-center justify-between p-6 border-b";
    
  const contentClass = isMobile 
    ? "p-4 max-h-96 overflow-y-auto"
    : "p-6 max-h-[60vh] overflow-y-auto";
    
  const mainCategoryButtonClass = isMobile 
    ? "w-full p-3 bg-gray-50 hover:bg-orange-50 rounded-lg flex items-center gap-3 transition border border-gray-200 hover:border-orange-200"
    : "flex flex-col items-center p-4 bg-gray-50 hover:bg-orange-50 rounded-xl transition border border-gray-200 hover:border-orange-200";

  // Helper function to get subcategory button class
  const getSubcategoryButtonClass = (subcategoryId) => {
    const baseClass = `w-full flex items-center justify-between ${
      isMobile ? 'p-3' : 'p-4'
    } rounded-lg transition border`;
    
    if (form.subcategory_id === String(subcategoryId)) {
      return `${baseClass} bg-orange-50 border-orange-300`;
    }
    
    return `${baseClass} bg-gray-50 hover:bg-orange-50 border-gray-200 hover:border-orange-200`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={containerClass}>
        {/* Modal Header with brand color */}
        <div className={headerClass} style={{ borderColor: colors.border }}>
          {form.category_id ? (
            <button
              onClick={() => onMainCategorySelect('')}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              style={{ color: colors.text }}
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <div className="w-10"></div>
          )}
          
          <h3 
            className={`${isMobile ? "text-base" : "text-lg"} font-semibold`}
            style={{ color: colors.text }}
          >
            {form.category_id ? 'Select Subcategory' : 'Select Category'}
          </h3>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            style={{ color: colors.text }}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className={contentClass}>
          {!form.category_id ? (
            <div>
              <h4 
                className={`${isMobile ? "text-sm" : "text-base"} font-medium mb-4`}
                style={{ color: colors.text }}
              >
                Main Categories
              </h4>
              
              {isMobile ? (
                <div className="space-y-2">
                  {mainCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => onMainCategorySelect(String(category.id))}
                      className={mainCategoryButtonClass}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: colors.primaryLight }}
                      >
                        {getCategoryIcon(category)}
                      </div>
                      <span 
                        className="font-medium"
                        style={{ color: colors.text }}
                      >
                        {category.name}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {mainCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => onMainCategorySelect(String(category.id))}
                      className={mainCategoryButtonClass}
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                        style={{ backgroundColor: colors.primaryLight }}
                      >
                        {getCategoryIcon(category)}
                      </div>
                      <span 
                        className="text-sm font-medium text-center"
                        style={{ color: colors.text }}
                      >
                        {category.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <h4 
                className={`${isMobile ? "text-sm" : "text-base"} font-medium mb-4`}
                style={{ color: colors.text }}
              >
                Subcategories
              </h4>
              
              <div className="space-y-2">
                {subcategories.map((subcategory) => (
                  <button
                    key={subcategory.id}
                    onClick={() => onSubcategorySelect(String(subcategory.id))}
                    className={getSubcategoryButtonClass(subcategory.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg flex items-center justify-center`}
                        style={{ 
                          backgroundColor: form.subcategory_id === String(subcategory.id) 
                            ? colors.primary 
                            : colors.primaryLight 
                        }}
                      >
                        {getCategoryIcon(subcategory)}
                      </div>
                      <span 
                        className="font-medium"
                        style={{ color: colors.text }}
                      >
                        {subcategory.name}
                      </span>
                    </div>
                    
                    {form.subcategory_id === String(subcategory.id) ? (
                      <FiCheck 
                        className="w-5 h-5" 
                        style={{ color: colors.primary }}
                      />
                    ) : (
                      <FiChevronRight className="w-5 h-5" style={{ color: colors.textLight }} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryModal;